import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { WifiOff, RefreshCw, Download, Share, PlusSquare, X } from "lucide-react";
import { useOnline } from "../lib/net";
import { useOutbox, flush } from "../lib/outbox";
import { useInstallPrompt, isIOSSafari, isInstalled } from "../lib/pwa";

export const OfflineBanner = ({ fromCache }) => {
  const online = useOnline();
  if (online) return null;
  return <div className="banner-offline" data-testid="offline-banner"><WifiOff size={14} /> Offline — showing saved jobs</div>;
};

const mmss = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

export const SyncStatus = () => {
  const { items, flushing, nextAt, paused } = useOutbox();
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(id); }, []);
  const pending = items.filter((i) => i.sync_state !== "failed").length;
  const failed = items.length - pending;
  if (!items.length) return null;
  return (
    <div className="sync-bar" data-testid="sync-status">
      <RefreshCw size={14} className={flushing ? "animate-spin" : ""} />
      <span className="flex-1 text-sm" data-testid="sync-text">
        <span>{flushing ? "Syncing…" : paused && pending ? `${pending} queued · sign in to sync` : pending ? `${pending} queued` : `${failed} need${failed === 1 ? "s" : ""} attention`}</span>{!flushing && !paused && pending && nextAt ? <span className="notranslate" translate="no"> · retrying in {mmss(nextAt - Date.now())}</span> : null}
      </span>
      {pending > 0 && !flushing && <button type="button" className="balance-pill" onClick={flush} data-testid="sync-now-button">Sync now</button>}
    </div>
  );
};

const KEY = "dropit-ios-install-dismissed";
export const InstallCard = ({ forceIOS = false, onClose }) => {
  const { canInstall, install } = useInstallPrompt();
  const [iosOpen, setIosOpen] = useState(() => forceIOS || (isIOSSafari() && Date.now() - Number(localStorage.getItem(KEY) || 0) > 7 * 86400000));
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setIosOpen(false); onClose?.(); };
  if (isInstalled()) return null;
  return (<>
    {canInstall && (
      <div className="card install-card fade-up" data-testid="install-card">
        <Download className="text-primary" size={22} />
        <div className="flex-1"><p className="font-bold">Install <span className="notranslate" translate="no">DropIt</span></p><p className="text-sm text-muted">Works offline, opens from your home screen.</p></div>
        <button type="button" className="btn-primary w-auto px-4" onClick={install} data-testid="install-button">Install</button>
      </div>
    )}
    {iosOpen && createPortal(
      <div className="sheet-backdrop" onClick={dismiss} data-testid="ios-install-backdrop">
        <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="ios-install-sheet">
          <div className="flex items-center justify-between"><p className="eyebrow">Install DropIt</p><button type="button" className="icon-btn" onClick={dismiss} data-testid="ios-install-dismiss" aria-label="Close"><X size={16} /></button></div>
          <h2 className="card-title">Add to your Home Screen</h2>
          <div className="ios-steps mt-4">
            <div className="ios-step" data-testid="ios-step-1"><span className="ios-num">1</span><Share size={26} className="text-primary" /><p>Tap <strong>Share</strong> ⎋ in Safari's toolbar</p></div>
            <div className="ios-step" data-testid="ios-step-2"><span className="ios-num">2</span><PlusSquare size={26} className="text-primary" /><p>Choose <strong>Add to Home Screen</strong> ➕</p></div>
          </div>
          <button type="button" className="btn-primary mt-5" onClick={dismiss} data-testid="ios-install-done">Got it</button>
        </div>
      </div>, document.body)}
  </>);
};
